// src/pages/auth/LoginPage.tsx
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "features/auth/hooks/useAuth";
import { getErrorMessage } from "utils/errors";

import {
  SEEDED_USERS,
  getDevAuthSession,
  isDevAuthEnabled,
  setDevAuthSession,
} from "devAuth";

import forgotQuestionIcon from "../../assets/HYSTERON_QUESTION.png";

type UnderlineInputProps = {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
  disabled?: boolean;
};

const UnderlineInput: React.FC<UnderlineInputProps> = ({
  id,
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  autoComplete,
  disabled
}) => (
  <div>
    <label htmlFor={id} className="font-display text-intro-text text-slate-900">
      {label}
    </label>

    <div className="mt-1 border-b border-black">
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        className={[
          "w-full bg-transparent py-2",
          "font-ui text-p-body-text text-slate-900",
          "outline-none",
          "placeholder:text-slate-400 placeholder:text-[15px]",
          "disabled:opacity-100"
        ].join(" ")}
      />
    </div>
  </div>
);

type DevAuthPickerProps = { onSignedIn: () => void };

const DevAuthPickerImpl: React.FC<DevAuthPickerProps> = ({ onSignedIn }) => {
  const initial = getDevAuthSession()?.sub ?? SEEDED_USERS[0]?.firebaseId ?? "";
  const [selected, setSelected] = useState<string>(initial);

  const signIn = () => {
    setDevAuthSession(selected);
    onSignedIn();
  };

  return (
    <div className="mb-8 rounded-2xl border-2 border-dashed border-amber-500 bg-amber-50 p-4 text-left">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber-700">
          DEV AUTH BYPASS (REACT_APP_DEV_AUTH=1)
        </span>
      </div>
      <p className="mb-3 font-body text-[13px] leading-snug text-slate-700">
        Impersonate a seeded backend user. No Firebase, no password — the browser sends an
        <code className="mx-1 rounded bg-amber-100 px-1 text-[11px]">X-Endpoint-API-UserInfo</code>
        header that the local backend trusts directly. Selection is persisted in
        <code className="mx-1 rounded bg-amber-100 px-1 text-[11px]">localStorage</code>.
      </p>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <select
          aria-label="Impersonate seeded user"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="flex-1 rounded-md border border-amber-400 bg-white px-3 py-2 font-body text-[14px] text-slate-900"
        >
          {SEEDED_USERS.map((u) => (
            <option key={u.firebaseId} value={u.firebaseId}>
              {u.email} — {u.archetype}
              {u.notes ? ` (${u.notes})` : ""}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={signIn}
          className="rounded-full bg-amber-600 px-5 py-2 font-manrope text-[12px] uppercase tracking-[0.18em] text-white hover:bg-amber-700"
        >
          Sign in as user
        </button>
      </div>
    </div>
  );
};

const DevAuthPickerNoop: React.FC<DevAuthPickerProps> = () => null;

// Module-level swap: in production the picker resolves to a no-op and
// DevAuthPickerImpl becomes unreferenced. CRA + Terser then drop the impl
// (and its "DEV AUTH BYPASS" JSX literals) from the bundle, while keeping
// all hook calls unconditional within whichever component is used at runtime.
const DevAuthPicker: React.FC<DevAuthPickerProps> =
  process.env.NODE_ENV === "production" ? DevAuthPickerNoop : DevAuthPickerImpl;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as any)?.from?.pathname || "/admin";

  const auth = useAuth() as any;
  const loginUser: ((email: string, password: string) => Promise<any>) | undefined =
    auth?.login ?? auth?.signIn ?? auth?.signin;

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  // Admin only: go back to the page that sent us here, else the admin home.
  // AdminRoute turns away an account without admin rights.
  const routeAdminPostLogin = () => {
    navigate(from, { replace: true });
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError("");

    if (!loginUser) {
      setLoginError("Sign in is not available.");
      return;
    }

    try {
      await loginUser(loginEmail.trim(), loginPassword);
      routeAdminPostLogin();
    } catch (err: any) {
      setLoginError(getErrorMessage(err, "Failed to login"));
    }
  };

  const devAuthOn = isDevAuthEnabled();

  return (
    <main className="relative min-h-screen px-4 pt-8 pb-32 sm:pt-10 sm:pb-40">
      <div className="mx-auto w-full max-w-7xl">
        {devAuthOn && (
          <div className="mx-auto mb-6 max-w-2xl">
            <DevAuthPicker onSignedIn={routeAdminPostLogin} />
          </div>
        )}

        <section className="mt-10 sm:mt-12 flex justify-center relative z-10">
          <div className="w-full md:w-2/5 max-w-none">
            <div className="min-w-0 bg-white rounded-3xl shadow-sm border border-slate-200 px-4 py-5 sm:px-5 sm:py-6">
              <h4
                className={[
                  "typo-h4-section-title uppercase",
                  "text-[#d8b2ce]",
                  "text-[12px] sm:text-[13px]",
                  "tracking-[0.08em]"
                ].join(" ")}
              >
                <span aria-hidden="true">{">"}</span>
                <span className="inline-block w-2" aria-hidden="true" />
                <span>ADMIN SIGN IN</span>
              </h4>

                  <form onSubmit={handleLogin} className="mt-4 flex flex-col gap-6">
                    <UnderlineInput
                      id="login-email"
                      label="Email"
                      value={loginEmail}
                      onChange={setLoginEmail}
                      placeholder="Registered email"
                      autoComplete="email"
                    />

                    <UnderlineInput
                      id="login-password"
                      label="Password"
                      type="password"
                      value={loginPassword}
                      onChange={setLoginPassword}
                      placeholder="Your password"
                      autoComplete="current-password"
                    />

                    <div className="flex justify-start">
                      <button
                        type="button"
                        onClick={() => navigate("/forgot-password")}
                        className={[
                          "inline-flex items-center gap-2",
                          "font-manrope font-normal",
                          "text-[11px] leading-none",
                          "uppercase tracking-[0.08em]",
                          "text-[#a273ab]"
                        ].join(" ")}
                      >
                        <img
                          src={forgotQuestionIcon}
                          alt=""
                          aria-hidden="true"
                          className="h-3 w-3 object-contain"
                          loading="lazy"
                          decoding="async"
                        />
                        <span>Forgot password?</span>
                      </button>
                    </div>

                    {loginError && <p className="typo-p-supporting-text">{loginError}</p>}

                    <button
                      type="submit"
                      className={[
                        "group relative overflow-hidden self-start",
                        "inline-flex items-center",
                        "w-full sm:w-72",
                        "rounded-full bg-[#d8b2ce] px-5 py-2.5 shadow-md",
                        "text-[#151A3A] hover:opacity-95 transition-opacity"
                      ].join(" ")}
                    >
                      <span className="typo-button-text-mobile sm:typo-button-text text-xs tracking-[0.08em] whitespace-nowrap text-left">
                        SIGN IN
                      </span>
                      <span
                        aria-hidden="true"
                        className="pointer-events-none ml-4 flex-1 flex items-center"
                      >
                        <span
                          className={[
                            "relative h-[7px] w-full",
                            "origin-left will-change-transform",
                            "transition-transform duration-300 ease-out",
                            "scale-x-100",
                            "group-hover:scale-x-[0.45]",
                            "group-active:scale-x-100 group-active:duration-150",
                          ].join(" ")}
                        >
                          <span className="absolute left-0 right-[7px] top-1/2 -translate-y-1/2 h-px bg-current rounded-full" />
                          <span className="absolute right-0 top-1/2 -translate-y-1/2">
                            <svg
                              className="h-[7px] w-[7px]"
                              viewBox="0 0 10 10"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M1 1L9 5L1 9"
                                stroke="currentColor"
                                strokeWidth="1"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        </span>
                      </span>
                    </button>
                  </form>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default LoginPage;
